'use client'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import type { Header } from '@/payload-types'
import { Logo } from '@/components/Logo/Logo'
import { X, User, Sun, Moon } from 'lucide-react'
import { CMSLink } from '@/components/Link'
import { useTheme } from '@/providers/Theme'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import ThemeButton from '@/components/ThemeButton'
import LanguageButton from '@/components/LanguageButton'

interface HeaderClientProps {
  header: Header
  lang?: 'en' | 'fr' | 'ar' | 'all' | undefined
}
interface LinkUrl {
  type?: ('reference' | 'custom') | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages'
    value: number | Page
  } | null
  url?: string | null
  label: string
}
export const HeaderClient = ({ header, lang }: HeaderClientProps) => {
  const pathname = usePathname()
  const { theme, setTheme } = useTheme()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const [scrolledMin, setScrolledMin] = useState(false)

  const loginLabel = header.loginLabel || 'Login'
  const loginLink = header.loginLink || '/admin/login'

  const segments = pathname.split('/').filter(Boolean)
  const isLocalizedHome = segments.length <= 1

  useEffect(() => {
    const handleScroll = () => {
      // On non home pages, force scrolledMin = true (immediate background)
      if (!isLocalizedHome) {
        setScrolledMin(true)
      } else {
        setScrolledMin(window.scrollY > 4)
      }
    }

    handleScroll() // run on mount
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLocalizedHome])

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  useEffect(() => {
    setIsMenuOpen(false)
  }, [pathname])

  const isActiveLink = (linkUrl: LinkUrl) => {
    if (linkUrl.type === 'reference') {
      if ('/' + linkUrl.reference?.value.slug === pathname) {
        return true
      }
    } else return pathname.startsWith(linkUrl.url)
  }

  return (
    <header
      className={`z-50 fixed w-full top-0 bg-primary
        ${
          isLocalizedHome && !scrolledMin
            ? 'bg-transparent'
            : scrolledMin
              ? 'bg-primary'
              : ''
        }
        
              
              transition-all duration-300`}
    >
      <div className="container">
        <div className="py-4 flex justify-between items-center">
          {/* Mobile Menu Button - Now on the left */}
          <button
            className="lg:hidden text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <img src="/icons/navIcon.svg" alt="" />
          </button>
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <Logo loading="eager" priority="high" className="h-10 w-auto" lang={lang} />
          </Link>
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center justify-center">
            <ul className="flex space-x-8">
              {header?.navItems?.map(({ link }, i) => (
                <li key={i} className="!mx-6 min-w-10">
                  <CMSLink
                    {...link}
                    appearance="none"
                    className={`text-white hover:text-gray-200 transition-colors w-full ${
                      isActiveLink(link) ? 'font-bold' : 'font-medium'
                    }`}
                  />
                </li>
              ))}
            </ul>
          </nav>
          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <ThemeButton theme={theme} setTheme={setTheme} className="rtl:ml-3" />
            <LanguageButton language={'fr'} />
            <Link href={loginLink} className="flex items-center text-white hover:text-gray-200">
              <User className="mr-2 rtl:ml-2 h-5 w-5" />
              <span>{loginLabel}</span>
            </Link>
            {/* <Link
              href={header['contactUsButton'] || '/register'}
              className="px-6 py-2 border-2 border-white rounded-full text-white hover:bg-white hover:text-[#0a4734] transition-colors font-medium"
            >
              Register as Agency
            </Link> */}
          </div>
          {/* Mobile User Icon - Now on the right */}
          <div className="lg:hidden flex item-center gap-2">
            <LanguageButton language={'fr'} />
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm lg:hidden z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile Menu - Full-screen design */}
      <div
        className={`fixed top-0 left-0 w-full h-full bg-white transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? 'translate-y-0' : '-translate-y-full'
        } lg:hidden z-50`}
      >
        {/* Menu Header */}
        <div className="bg-[#0a4734] text-white py-6 px-4 flex items-center justify-between">
          <button
            onClick={() => setIsMenuOpen(false)}
            className="text-white"
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center">
            <Logo loading="eager" priority="high" className="h-10 w-auto" lang={lang} />
          </div>

          <Link href="/admin/login" className="text-white">
            <User className="h-6 w-6" />
          </Link>
        </div>

        {/* Navigation Items - with gap after header */}
        <div className="mt-12">
          {header?.navItems?.map(({ link }, i) => (
            <div
              key={i}
              onClick={() => {
                setIsMenuOpen(false)
              }}
              className={`w-full relative ${isActiveLink(link) ? 'bg-[#b3d1c3]' : ''}`}
            >
              <Link
                href={link.url || '/'}
                className="block py-5 px-4 text-2xl font-bold text-black"
              >
                {link.label}
              </Link>
              <span className="absolute right-6 top-1/2 transform -translate-y-1/2 text-2xl">
                ›
              </span>
            </div>
          ))}
        </div>
      </div>
    </header>
  )
}
